/**
 * 발주처(매입처)별 결제조건 관리 (2026-07-29, v2.023).
 *
 *   문서함 > 발주서 > [💳 발주처 결제조건] 에서 연다.
 *   여기 등록해 두면 발주서 작성에서 [발주서 수신(매입처)] 을 넣는 순간 결제조건이 자동으로 채워진다.
 *   등록이 없는 매입처는 기본값(DEFAULT_PAYMENT_TERM)을 쓴다.
 *
 *   매입처 표기 차이('(주)'·띄어쓰기 등)는 서버의 norm_company 가 흡수하므로
 *   화면에서 이름을 다듬을 필요가 없다.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_PAYMENT_TERM,
  deletePoPaymentTerm,
  fetchPoSuppliers,
  listPoPaymentTerms,
  savePoPaymentTerm,
  type PoPaymentTermRow,
} from '../../lib/po-api';
import { DraggableModal } from '../../components/DraggableModal';
import { SimpleAutocomplete } from '../../components/SimpleAutocomplete';
import { useDirtyClose } from '../../lib/useDirtyClose';
import { LoadingLines } from '../../components/ui/Skeleton';

interface Props {
  canEdit: boolean;
  onClose: () => void;
}

export function PoPaymentTermModal({ canEdit, onClose }: Props) {
  // 작성 중 닫힘 방지 — ESC·[×] 가 requestClose 를 거친다 (v2.163.0).
  //   줄은 [+ 등록]/[수정] 으로 그때그때 저장되므로, 저장에 성공하면 resetDirty 로 물음을 푼다
  //   (안 그러면 다 저장해 놓고 닫을 때마다 물어 잔소리가 된다).
  const { markDirty, resetDirty, requestClose } = useDirtyClose(onClose);
  const [rows, setRows] = useState<PoPaymentTermRow[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [query, setQuery] = useState('');

  // 입력칸 — 기존 행의 [수정]을 누르면 그 값이 여기로 들어온다(매입처 1곳당 1건이라 upsert).
  const [supplier, setSupplier] = useState('');
  const [term, setTerm] = useState('');
  const [saving, setSaving] = useState(false);

  async function reload() {
    setErr('');
    try {
      const [list, sup] = await Promise.all([listPoPaymentTerms(), fetchPoSuppliers()]);
      setRows(list);
      setSuppliers(sup);
    } catch (e) {
      setErr((e as Error).message || '불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.supplierName.toLowerCase().includes(q) || r.paymentTerm.toLowerCase().includes(q),
    );
  }, [rows, query]);

  // 이미 등록된 매입처를 다시 고르면 '수정'이 된다 — 사용자가 헷갈리지 않게 미리 알려준다.
  const existing = useMemo(() => {
    const s = supplier.trim();
    if (!s) return null;
    return rows.find((r) => r.supplierName === s) ?? null;
  }, [rows, supplier]);

  async function onSave() {
    const s = supplier.trim();
    const t = term.trim();
    if (!s) { setErr('매입처를 고르거나 입력하세요'); return; }
    if (!t) { setErr('결제조건을 입력하세요'); return; }
    setSaving(true); setErr(''); setMsg('');
    try {
      await savePoPaymentTerm(s, t);
      setMsg(`✓ ${s} — ${t}`);
      setSupplier(''); setTerm('');
      resetDirty();
      await reload();
    } catch (e) {
      setErr((e as Error).message || '저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(r: PoPaymentTermRow) {
    if (!window.confirm(`${r.supplierName} 의 결제조건을 삭제할까요?\n\n삭제하면 이 매입처는 발주서 작성 시 기본값(${DEFAULT_PAYMENT_TERM})을 씁니다.`)) return;
    setErr(''); setMsg('');
    try {
      await deletePoPaymentTerm(r.id);
      setMsg(`✓ ${r.supplierName} 삭제됨`);
      await reload();
    } catch (e) {
      setErr((e as Error).message || '삭제하지 못했습니다');
    }
  }

  return (
    <DraggableModal onClose={requestClose} initialWidth={860} initialHeight={620} closeOnEsc={false}>
      <div className="flex flex-col h-full" onInput={markDirty} onChange={markDirty}>
        <div className="dm-drag-handle px-5 py-3 border-b border-slate-200 flex items-center justify-between cursor-move">
          <h3 className="ui-subsection-title">💳 발주처 결제조건</h3>
          <button type="button" onClick={requestClose} className="ui-close">×</button>
        </div>

        <div className="px-5 py-2 text-xs text-slate-500 border-b border-slate-100">
          매입처마다 결제조건을 등록해 두면, <strong className="text-slate-700">발주서 작성</strong>에서 [발주서 수신(매입처)]을 넣는 순간
          결제조건이 <strong className="text-slate-700">자동으로 채워집니다</strong>. 등록하지 않은 매입처는{' '}
          <strong className="text-slate-700">{DEFAULT_PAYMENT_TERM}</strong> 로 채워집니다.
          회사 이름의 <strong className="text-slate-700">(주)·띄어쓰기 차이는 자동으로 맞춰</strong> 찾습니다.
        </div>

        {canEdit && (
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-72">
                <div className="text-xs text-slate-500 mb-1">매입처</div>
                <SimpleAutocomplete
                  value={supplier}
                  onChange={setSupplier}
                  candidates={suppliers}
                  placeholder="매입처 검색 또는 직접 입력"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                />
              </div>
              <div className="flex-1 min-w-[260px]">
                <div className="text-xs text-slate-500 mb-1">결제조건</div>
                <input
                  type="text"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="예: 익월말 현금결제 / 납품 후 30일 / 선급 50% + 잔금 검수 후"
                  maxLength={200}
                  className="ui-input w-full"
                />
              </div>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? '저장 중…' : existing ? '수정' : '+ 등록'}
              </button>
            </div>
            {existing && (
              <div className="mt-1.5 text-xs text-amber-700">
                이미 등록된 매입처입니다 — 저장하면 <strong>{existing.paymentTerm}</strong> 에서 바뀝니다.
              </div>
            )}
          </div>
        )}

        {(err || msg) && (
          <div className={`mx-5 mt-3 px-3 py-2 rounded text-sm border ${err ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {err || msg}
          </div>
        )}

        <div className="px-5 pt-3 flex items-center gap-2">
          <div className="text-sm font-medium text-slate-700 mr-auto">등록된 매입처 ({rows.length}곳)</div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="매입처·결제조건 검색"
            className="ui-input w-56"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-auto px-5 py-3">
          <table className="w-full text-[13px] border border-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="border border-slate-200 px-2 py-1.5 min-w-[220px]">매입처</th>
                <th className="border border-slate-200 px-2 py-1.5 min-w-[240px]">결제조건</th>
                <th className="border border-slate-200 px-2 py-1.5 w-32 whitespace-nowrap">최근 수정</th>
                {canEdit && <th className="border border-slate-200 px-2 py-1.5 w-24 text-center">관리</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canEdit ? 4 : 3} className="text-center text-slate-400 py-8"><LoadingLines /></td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 4 : 3} className="text-center text-slate-400 py-8">
                    {rows.length === 0 ? '등록된 결제조건이 없습니다 — 모든 발주서가 기본값을 씁니다' : '검색 결과가 없습니다'}
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="border border-slate-200 px-2 py-1.5">{r.supplierName}</td>
                  <td className="border border-slate-200 px-2 py-1.5">{r.paymentTerm}</td>
                  <td className="border border-slate-200 px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">
                    {r.updatedAt.slice(0, 10)}
                  </td>
                  {canEdit && (
                    <td className="border border-slate-200 px-2 py-1.5">
                      <div className="flex gap-1 justify-center">
                        <button
                          type="button"
                          onClick={() => { setSupplier(r.supplierName); setTerm(r.paymentTerm); setErr(''); setMsg(''); }}
                          title="위 입력칸으로 불러오기"
                          className="px-1.5 text-slate-500 hover:text-blue-600"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={() => void onDelete(r)}
                          title="삭제"
                          className="px-1.5 text-slate-400 hover:text-rose-600"
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DraggableModal>
  );
}
